import { useMemo } from 'react';

export type AuthMode = 'access' | 'local';

export interface AdminConfig {
  apiBaseUrl: string;
  authMode: AuthMode;
  localToken: string;
}

export function useAdminConfig(): AdminConfig {
  return useMemo(() => {
    const apiBaseUrl =
      import.meta.env.VITE_CANISX_ADMIN_API_BASE_URL ??
      (typeof window !== 'undefined' ? window.location.origin : '');
    const authMode = (import.meta.env.VITE_CANISX_ADMIN_AUTH_MODE as AuthMode) ?? 'access';
    const localToken = import.meta.env.VITE_CANISX_ADMIN_API_LOCAL_TOKEN ?? '';
    return { apiBaseUrl, authMode, localToken };
  }, []);
}

export function isConfigValid(config: AdminConfig): boolean {
  return typeof config.apiBaseUrl === 'string' && config.apiBaseUrl.length > 0;
}

export function getAuthHeaders(config: AdminConfig): Record<string, string> {
  if (config.authMode === 'local' && config.localToken) {
    return { 'X-CanisX-Admin-Token': config.localToken };
  }
  return {};
}

/**
 * The backend does not yet return a per-launch capability flag for clinic
 * frontend access-code readiness. Until it does, create-time stable-URL
 * sharing copy/open readiness is gated behind this deploy-time flag rather
 * than shown unconditionally (R19, KTD10). Durable detail still renders a
 * valid backend stable URL when present so operators can recover it later.
 * Defaults to not-ready so deployed builds fail closed.
 */
export function isStableDemoUrlShareReady(): boolean {
  return import.meta.env.VITE_CANISX_ADMIN_STABLE_URL_SHARE_READY === 'true';
}

export type AdminEnv = 'prod' | 'dev' | 'local';

/**
 * Derives the adminpanel environment from the browser hostname, mirroring
 * the env-badge convention in App.tsx: the known prod host is `prod`, the
 * known dev host (and any preview/branch alias under it) is `dev`, and
 * anything else (localhost, unrecognized preview hosts) is `local`. Used to
 * scope which stable-demo-URL hosts are accepted per environment (R4).
 */
export function getAdminEnv(): AdminEnv {
  if (typeof window === 'undefined') return 'local';
  const host = window.location.hostname;
  if (host === 'adminpanel.canisx.com') return 'prod';
  if (host === 'adminpanel-dev.canisx.com' || host.includes('adminpanel-dev.')) return 'dev';
  return 'local';
}
